import React, { useMemo } from 'react';
import { Box, Divider, Flex, Text } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';

import { calculateMargin } from '../../helpers/calculateMargin';
import { TableCellDetailPopover } from '../../components/requests/table-cell-detail';
import { useAppConfig } from '../../hooks/useAppConfig';
import { When } from '../../components/common';
import type { SurveyItemRow } from './types';

type Props = {
    row: SurveyItemRow;
    showCashbuildPrice: boolean;
    showPnlPrice: boolean;
};

function formatSurveyMarginPercent(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
        return '—';
    }
    return `${value.toFixed(2)}%`;
}

type MarginDisplayProps = {
    label: string;
    value: number | null | undefined;
    isPositive?: boolean;
    showStatusIcon?: boolean;
};

function MarginDisplay({
    label,
    value,
    isPositive,
    showStatusIcon = false,
}: MarginDisplayProps) {
    const formatted = formatSurveyMarginPercent(value);
    const hasValue = value != null && Number.isFinite(value);

    return (
        <div className="flex items-center justify-between">
            <Text className="uppercase text-[11px] text-[#7D8FB3] font-bold leading-tight">
                {label}
            </Text>
            <When
                condition={hasValue && showStatusIcon}
                fallback={
                    <Text
                        className={`font-bold text-sm ${
                            hasValue ? 'text-primary' : 'text-[#7D8FB3]'
                        }`}
                    >
                        {formatted}
                    </Text>
                }
            >
                <div className="flex items-center gap-1">
                    <When
                        condition={Boolean(isPositive)}
                        fallback={
                            <IconAlertTriangle
                                className="text-amber-500"
                                size={16}
                            />
                        }
                    >
                        <IconCircleCheck className="text-green-500" size={16} />
                    </When>
                    <Text
                        className={`font-bold text-sm ${
                            isPositive ? 'text-green-500' : 'text-amber-600'
                        }`}
                    >
                        {formatted}
                    </Text>
                </div>
            </When>
        </div>
    );
}

function marginForPrice(
    costPrice: number | null | undefined,
    sellingPrice: number | null | undefined,
    proposedPrice: number | undefined,
    benchmarkMargin: number | undefined,
    vatPercentage: number,
): { currentMargin: number | null; proposedMargin: number | null } {
    if (costPrice == null || sellingPrice == null) {
        return { currentMargin: null, proposedMargin: null };
    }

    const { currentMargin, newMargin } = calculateMargin({
        costPrice,
        currentPrice: sellingPrice,
        proposedPrice,
        benchmarkMargin,
        vatPercentage,
    });

    return {
        currentMargin,
        proposedMargin:
            proposedPrice != null && proposedPrice > 0 ? newMargin : null,
    };
}

export function SurveyMarginTableCell({
    row,
    showCashbuildPrice,
    showPnlPrice,
}: Props) {
    const { vatPercentage: fallbackVatPercentage } = useAppConfig();

    const marginModel = useMemo(() => {
        const vatPercentage =
            row.resolvedVatPercentage ?? fallbackVatPercentage;
        const proposedPrice =
            row.proposedSellingPrice ??
            row.priceChangeRequest?.proposedPrice ??
            undefined;
        const benchmarkMargin = row.benchmarkMargin ?? undefined;
        const showProposedMargin = proposedPrice != null && proposedPrice > 0;

        const cashbuildMargins = marginForPrice(
            row.cashbuildCostPrice ?? row.currentCostPrice,
            row.currentCashbuildPrice ??
                (showPnlPrice ? null : row.currentPrice),
            proposedPrice,
            benchmarkMargin,
            vatPercentage,
        );
        const pnlMargins = marginForPrice(
            row.pnlCostPrice ?? row.currentCostPrice,
            row.currentPnlPrice,
            proposedPrice,
            benchmarkMargin,
            vatPercentage,
        );
        const proposedMargins = marginForPrice(
            row.currentCostPrice,
            row.currentPrice,
            proposedPrice,
            benchmarkMargin,
            vatPercentage,
        );

        const hasAnyCurrentMargin =
            (showCashbuildPrice && cashbuildMargins.currentMargin != null) ||
            (showPnlPrice && pnlMargins.currentMargin != null) ||
            proposedMargins.currentMargin != null;

        if (!hasAnyCurrentMargin && !showProposedMargin) {
            return null;
        }

        return {
            cashbuildCurrentMargin: cashbuildMargins.currentMargin,
            pnlCurrentMargin: pnlMargins.currentMargin,
            proposedMargin: proposedMargins.proposedMargin,
            benchmarkMargin: row.benchmarkMargin,
            showProposedMargin,
        };
    }, [row, fallbackVatPercentage, showCashbuildPrice, showPnlPrice]);

    if (!marginModel) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        );
    }

    const {
        cashbuildCurrentMargin,
        pnlCurrentMargin,
        proposedMargin,
        benchmarkMargin,
        showProposedMargin,
    } = marginModel;
    const benchmark = benchmarkMargin ?? 0;

    const trigger = (
        <div className="flex h-full w-full min-h-0 items-center justify-between gap-2">
            <Text size="sm" c="primary" fw={600} className="hover:underline">
                View Margins
            </Text>
        </div>
    );

    return (
        <div className="flex w-full justify-start">
            <TableCellDetailPopover
                title="Margins"
                trigger={trigger}
                triggerClassName="bg-transparent border-0 shadow-none hover:bg-transparent p-0 h-auto w-fit"
            >
                <Box bg="white" p="md" className="min-w-[240px]">
                    <Flex direction="column" gap="sm">
                        <When condition={showCashbuildPrice}>
                            <MarginDisplay
                                label="Current Cashbuild Margin"
                                value={cashbuildCurrentMargin}
                                isPositive={
                                    cashbuildCurrentMargin != null &&
                                    cashbuildCurrentMargin > benchmark
                                }
                                showStatusIcon
                            />
                        </When>
                        <When condition={showCashbuildPrice && showPnlPrice}>
                            <Divider color="#e6e9ef" />
                        </When>
                        <When condition={showPnlPrice}>
                            <MarginDisplay
                                label="Current P&L Margin"
                                value={pnlCurrentMargin}
                                isPositive={
                                    pnlCurrentMargin != null &&
                                    pnlCurrentMargin > benchmark
                                }
                                showStatusIcon
                            />
                        </When>
                        <When condition={showProposedMargin}>
                            <>
                                <Divider color="#e6e9ef" />
                                <MarginDisplay
                                    label="Proposed Margin"
                                    value={proposedMargin}
                                    isPositive={
                                        proposedMargin != null &&
                                        proposedMargin > benchmark
                                    }
                                    showStatusIcon
                                />
                            </>
                        </When>
                        <Divider color="#e6e9ef" />
                        <MarginDisplay
                            label="Benchmark Margin"
                            value={benchmarkMargin}
                        />
                    </Flex>
                </Box>
            </TableCellDetailPopover>
        </div>
    );
}
